import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Repeat, Plus, Building2, Loader2, Ban, TrendingUp, CalendarClock, Users } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const SUB_STATUS = { active: ["Actif", "#10B981"], pending: ["En attente", "#F59E0B"], canceled: ["Résilié", "#71717a"], past_due: ["Dépassé", "#EF4444"] };
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function AbonnementsPage() {
  const { selectedId, selectedCompany } = useCompany();
  const cid = selectedId !== "all" ? selectedId : "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    if (!cid) { setLoading(false); return; }
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/companies/${cid}/subscriptions`).then((r) => r.data),
        api.get(`/catalog`, { params: { company_id: cid } }).then((r) => r.data).catch(() => []),
      ]);
      setData(d); setCatalog(c.filter((i) => i.active && i.sale_mode === "subscription"));
    } finally { setLoading(false); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post(`/companies/${cid}/subscriptions`, { client_email: dialog.client_email, item_id: dialog.item_id });
      setDialog(null); load(); toast.success("Abonnement créé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (id) => { if (!window.confirm("Résilier cet abonnement ?")) return; await api.delete(`/subscriptions/${id}`); load(); };

  if (!cid) return <div className="h-full flex items-center justify-center bg-background p-8" data-testid="subs-no-company"><div className="text-center max-w-sm"><Building2 size={44} strokeWidth={1} className="mx-auto mb-3 text-zinc-500" /><p className="text-zinc-400 text-sm">Sélectionnez une entreprise pour gérer ses abonnements.</p></div></div>;

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2.5"><Repeat size={22} className="text-klein" /><h1 className="font-cabinet font-black text-3xl tracking-tight">Abonnements</h1></div>
          <button data-testid="sub-new" onClick={() => setDialog({ client_email: "", item_id: catalog[0]?.id || "" })} className="flex items-center gap-1.5 h-10 px-4 rounded-lg bg-klein text-zinc-950 font-semibold text-sm hover:brightness-110"><Plus size={16} /> Nouvel abonnement</button>
        </div>
        <p className="text-zinc-400 text-sm mb-6">Revenus récurrents — {selectedCompany?.name}</p>

        {loading ? <div className="py-16 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="subs-kpis">
              <Kpi icon={<TrendingUp size={16} />} label="MRR" value={eur(data?.mrr)} accent />
              <Kpi icon={<CalendarClock size={16} />} label="ARR" value={eur(data?.arr)} />
              <Kpi icon={<Repeat size={16} />} label="Abonnements actifs" value={data?.active_count || 0} />
              <Kpi icon={<Users size={16} />} label="Total" value={data?.total_count || 0} />
            </div>

            {(data?.subscriptions || []).length === 0 ? <div className="py-16 text-center text-zinc-400"><Repeat size={42} strokeWidth={1} className="mx-auto mb-3" /><p className="text-sm">Aucun abonnement. Créez-en un ou laissez vos clients souscrire depuis la boutique.</p></div> : (
              <div className="rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="subs-table">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-zinc-400 text-xs border-b border-white/10"><th className="px-4 py-3">Client</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Prochaine échéance</th><th className="px-4 py-3 text-right">Montant</th><th className="px-4 py-3"></th></tr></thead>
                  <tbody>
                    {data.subscriptions.map((s) => {
                      const [label, col] = SUB_STATUS[s.status] || SUB_STATUS.active;
                      return (
                        <tr key={s.id} data-testid={`sub-row-${s.id}`} className="border-b border-white/[0.06]">
                          <td className="px-4 py-3"><div className="font-medium">{s.client_name}</div><div className="text-xs text-zinc-500">{s.client_email}</div></td>
                          <td className="px-4 py-3">{s.item_name}<span className="text-zinc-500 text-xs"> / {s.interval === "year" ? "an" : "mois"}</span></td>
                          <td className="px-4 py-3"><span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: col + "22", color: col }}>{label}</span></td>
                          <td className="px-4 py-3 text-zinc-400">{s.status === "active" ? fmtDay(s.next_due_date) : "—"}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium">{eur(s.amount)}</td>
                          <td className="px-4 py-3 text-right">{s.status !== "canceled" && <button data-testid={`sub-del-${s.id}`} onClick={() => cancel(s.id)} className="p-1.5 rounded hover:bg-white/10 text-signal" title="Résilier"><Ban size={14} /></button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDialog(null)}>
          <div className="bg-card border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="sub-dialog">
            <h2 className="font-cabinet font-bold text-lg mb-4">Nouvel abonnement</h2>
            <div className="space-y-3">
              <div><label className="block text-[11px] font-medium text-zinc-400 mb-1">Email du client</label><input data-testid="sub-client-email" value={dialog.client_email} onChange={(e) => setDialog({ ...dialog, client_email: e.target.value })} placeholder="client@exemple.com" className="sinp" /></div>
              <div><label className="block text-[11px] font-medium text-zinc-400 mb-1">Service (abonnement du catalogue)</label>
                <select data-testid="sub-item" value={dialog.item_id} onChange={(e) => setDialog({ ...dialog, item_id: e.target.value })} className="sinp">
                  <option value="">Choisir…</option>
                  {catalog.map((c) => <option key={c.id} value={c.id}>{c.name} — {eur(c.unit_price * (1 + c.vat_rate / 100))} / {c.interval === "year" ? "an" : "mois"}</option>)}
                </select>
                {catalog.length === 0 && <p className="text-xs text-amber-400 mt-1">Aucun article « abonnement » dans le catalogue. Créez-en un (mode de vente = Abonnement).</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setDialog(null)} className="h-9 px-4 rounded-lg border border-white/10 text-sm">Annuler</button>
              <button data-testid="sub-create" onClick={create} disabled={!dialog.client_email?.trim() || !dialog.item_id} className="h-9 px-4 rounded-lg bg-klein text-zinc-950 font-semibold text-sm disabled:opacity-50">Créer</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.sinp{width:100%;background:hsl(var(--background));border:1px solid hsl(var(--border));border-radius:8px;padding:8px 10px;font-size:14px;outline:none}.sinp:focus{box-shadow:0 0 0 2px rgba(0,47,167,.2)}`}</style>
    </div>
  );
}

function Kpi({ icon, label, value, accent }) {
  return (
    <div className="bg-card border border-white/10 rounded-2xl p-4">
      <div className="flex items-center gap-1.5 text-zinc-400 mb-1.5">{icon}<span className="font-mono-label text-[10px] uppercase tracking-widest">{label}</span></div>
      <div className={`font-cabinet font-black text-2xl ${accent ? "text-klein" : ""}`}>{value}</div>
    </div>
  );
}
